import React, { useState, useEffect, useCallback } from 'react';
import { KeyRound, ShieldAlert, Loader2, RefreshCw, ScrollText, Activity, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

const formatWhen = (iso) => {
  try {
    return new Date(iso).toLocaleString('pt-PT');
  } catch {
    return iso;
  }
};

export const SecurityTab = () => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rotating, setRotating] = useState(false);

  const [attempts, setAttempts] = useState([]);
  const [stats, setStats] = useState({ failures_last_15_min: 0, failures_last_24_h: 0, distinct_emails_24h: 0 });
  const [loadingAttempts, setLoadingAttempts] = useState(true);
  const [refreshingAttempts, setRefreshingAttempts] = useState(false);

  const loadAttempts = useCallback(async (silent = false) => {
    if (silent) setRefreshingAttempts(true); else setLoadingAttempts(true);
    try {
      const { data } = await api.get('/super-admin/login-attempts?limit=20&success=false');
      setAttempts(data.attempts || []);
      setStats(data.stats || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar tentativas de login');
    } finally {
      if (silent) setRefreshingAttempts(false); else setLoadingAttempts(false);
    }
  }, []);

  useEffect(() => {
    loadAttempts();
  }, [loadAttempts]);

  const handleRotate = async () => {
    setRotating(true);
    try {
      await api.post('/super-admin/rotate-jwt-secret');
      toast.success('Segredo JWT rodado', {
        description: 'Todas as sessões foram invalidadas. Vais ser redirecionado para o login.',
        duration: 4000,
      });
      setConfirmOpen(false);
      setTimeout(() => {
        window.location.href = '/login?rotated=1';
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao rodar segredo JWT');
    } finally {
      setRotating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="security-tab">
      {/* Recent failed logins */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ScrollText className="w-5 h-5 text-slate-700" />
                Tentativas de Login Falhadas
              </CardTitle>
              <CardDescription>
                Detecção de força bruta. IP é hashado (SHA-256, 16 chars) para proteger PII.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadAttempts(true)}
              disabled={refreshingAttempts}
              data-testid="refresh-login-attempts"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshingAttempts ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className={`rounded-lg border p-3 ${stats.failures_last_15_min >= 5 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className="text-xs text-slate-500 flex items-center gap-1">
                <Activity className="w-3 h-3" />
                Últimos 15 min
              </div>
              <div className={`text-2xl font-bold ${stats.failures_last_15_min >= 5 ? 'text-red-700' : 'text-slate-800'}`} data-testid="failures-15m">
                {stats.failures_last_15_min ?? 0}
              </div>
            </div>
            <div className="rounded-lg border p-3 bg-slate-50 border-slate-200">
              <div className="text-xs text-slate-500">Últimas 24h</div>
              <div className="text-2xl font-bold text-slate-800" data-testid="failures-24h">
                {stats.failures_last_24_h ?? 0}
              </div>
            </div>
            <div className="rounded-lg border p-3 bg-slate-50 border-slate-200">
              <div className="text-xs text-slate-500">Emails distintos (24h)</div>
              <div className="text-2xl font-bold text-slate-800" data-testid="distinct-emails-24h">
                {stats.distinct_emails_24h ?? 0}
              </div>
            </div>
          </div>

          {loadingAttempts ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : attempts.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <ShieldAlert className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>Sem tentativas falhadas registadas.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="login-attempts-table">
                <thead className="border-b bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Quando</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Email</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">IP (hash)</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {attempts.map((a, idx) => (
                    <tr key={idx} className="border-b hover:bg-slate-50" data-testid={`attempt-row-${idx}`}>
                      <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{formatWhen(a.created_at)}</td>
                      <td className="px-3 py-2 text-slate-700">{a.email || '—'}</td>
                      <td className="px-3 py-2 text-slate-500 font-mono text-xs">{a.ip_hash || '—'}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-red-700 border-red-200 bg-red-50 gap-1">
                          <XCircle className="w-3 h-3" />
                          Falha
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rotate JWT secret */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-amber-600" />
            Segredo de assinatura JWT
          </CardTitle>
          <CardDescription>
            Chave usada para assinar e verificar todos os tokens de sessão da plataforma.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert className="bg-amber-50 border-amber-200">
            <ShieldAlert className="w-4 h-4 text-amber-600" />
            <AlertDescription className="text-amber-800 text-sm">
              <strong>Aviso:</strong> Ao rodar o segredo JWT, <strong>TODAS as sessões activas
              (incluindo a tua) serão imediatamente invalidadas</strong>. Toda a gente terá de
              fazer login novamente. O novo segredo é persistido na base de dados e sobrevive
              a restarts do servidor.
            </AlertDescription>
          </Alert>

          <div className="rounded-lg border p-4 bg-slate-50">
            <p className="text-sm text-slate-700">
              <strong>Quando rodar?</strong>
            </p>
            <ul className="text-sm text-slate-600 mt-2 space-y-1 list-disc pl-5">
              <li>Suspeita de compromisso (ex.: dev/administrador com acesso deixou a organização)</li>
              <li>Rotina periódica de segurança (recomendado a cada 6 meses)</li>
              <li>Primeira configuração em produção quando não podes definir <code className="text-xs bg-slate-200 px-1 rounded">JWT_SECRET</code> nas Environment Variables (o novo segredo fica na base de dados e sobrevive ao próximo redeploy)</li>
            </ul>
          </div>

          <Button
            variant="destructive"
            onClick={() => setConfirmOpen(true)}
            data-testid="rotate-jwt-btn"
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Rodar Segredo JWT agora
          </Button>
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="rotate-jwt-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
              <ShieldAlert className="w-5 h-5" />
              Rodar Segredo JWT?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>Estás prestes a rodar o segredo de assinatura JWT.</p>
                <p><strong>Consequência imediata:</strong> Todos os utilizadores logados (incluindo tu) serão desligados.</p>
                <p className="text-slate-600">
                  Ninguém perde dados. Basta fazer login novamente com as credenciais existentes.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={rotating}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRotate}
              disabled={rotating}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="confirm-rotate-jwt-btn"
            >
              {rotating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Sim, rodar agora
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
